import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Popup from 'src/components/Popup/Popup'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  OrgExpenseReportConfirmDateChangePopup,
  OrgExpenseReportConfirmDateChangePopupProps,
} from 'src/models/orgExpenseReports.models'
import { createSetConfirmOrgExpenseReportDateChangePopup } from 'src/redux/actions/app.actions'
import { createSetOrgExpenseReportDateUpdateConfirmed } from 'src/redux/actions/data/orgExpenseReports.actions'
import { css } from 'styled-components'

const ConfirmOrgExpenseReportDateChangePopup: React.FC<
  OrgExpenseReportConfirmDateChangePopupProps
> = ({ type, date, rcnName }) => {
  const translate = useTranslate()

  const dispatch = useFlyoutDispatch()

  const body1 = (): React.ReactNode => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return translate(
          'You’ve chosen a closing date in the past. This report will include transactions through ${endDate}.',
          {
            endDate: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {date.format('ll')}
              </span>
            ),
          },
        )
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return translate(
          'You’ve chosen a closing date in the future. ${bolded} that ends on ${endDate}.',
          {
            bolded: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate(
                  'This will merge this report and your current report into one report',
                )}
              </span>
            ),
            endDate: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {date.format('ll')}
              </span>
            ),
          },
        )
    }
  }

  const body2 = (): React.ReactNode => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return translate(
          'To ensure all calendar days are accounted for, ${bolded} that starts on ${startDate} and closes according to your cycle settings for ${rcnName}.',
          {
            bolded: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('this will automatically create a new report')}
              </span>
            ),
            startDate: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {date.add(1, 'day').format('ll')}
              </span>
            ),
            rcnName: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {rcnName}
              </span>
            ),
          },
        )
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return translate(
          'Your next report for ${rcnName} will be generated on ${startDate}.',
          {
            rcnName: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {rcnName}
              </span>
            ),
            startDate: (
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {date.add(1, 'day').format('ll')}
              </span>
            ),
          },
        )
    }
  }

  const buttonText = (): React.ReactNode => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return translate('Yes, update this report')
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return translate('Yes, make this my current report')
    }
  }

  return (
    <Popup
      noPadding
      onClose={(): void => {
        dispatch(createSetConfirmOrgExpenseReportDateChangePopup(undefined))
      }}
    >
      <div
        css={css`
          position: relative;
          padding: 60px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: ${styleConstants.heights.majorInternalGutter};
          `}
        >
          {translate('Are you sure?')}
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: ${styleConstants.heights.majorInternalGutter};
            display: flex;
            flex-direction: column;
            gap: 20px;
          `}
        >
          <span>{body1()}</span>
          <span>{body2()}</span>
          <span>
            {translate(
              'All transactions will keep their expense review status.',
            )}
          </span>
        </div>
        <div
          css={css`
            display: flex;
            gap: 20px;
            align-items: center;
          `}
        >
          <Button
            testId={'confirmBtn'}
            type={ButtonTypes.button}
            design={ButtonDesigns.designCta}
            big={true}
            text={buttonText()}
            onClick={(): void => {
              dispatch(createSetOrgExpenseReportDateUpdateConfirmed(true))
              dispatch(
                createSetConfirmOrgExpenseReportDateChangePopup(undefined),
              )
            }}
            loading={false}
          />
          <HoverableLink
            testId={'cancelBtn'}
            underline
            ariaLabelSource={'No, let me choose another date'}
            onClick={(): void => {
              dispatch(
                createSetConfirmOrgExpenseReportDateChangePopup(undefined),
              )
            }}
          >
            {translate('No, let me choose another date')}
          </HoverableLink>
        </div>
      </div>
    </Popup>
  )
}

export default ConfirmOrgExpenseReportDateChangePopup
